import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { Activity, TrendingDown, TrendingUp, RotateCcw } from 'lucide-react';
import { usePaperTrading } from '../hooks/usePaperTrading';
import { useMobile } from '../hooks/useResponsiveDesign';
import { useTranslation } from '@/hooks/useTranslation';
import PaperTradingReset from './PaperTradingReset';

const parseNumeric = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const getPerformanceColor = (value: number) => {
  if (value > 0) return 'text-green-600 dark:text-green-400';
  if (value < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-400';
};

interface RealTimePerformanceMetricsProps {
  mode: 'paper' | 'live';
  baseBalance: number | null;
  winRate: number | null;
  unrealizedPnL: number;
}

const RealTimePerformanceMetrics: React.FC<RealTimePerformanceMetricsProps> = ({
  mode,
  baseBalance: rawBaseBalance,
  winRate: rawWinRate,
  unrealizedPnL: rawUnrealizedPnL,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { refreshData } = usePaperTrading();

  const [showResetModal, setShowResetModal] = useState(false);

  const translateWithFallback = useCallback(
    (key: string, fallback: string) => {
      const translated = t(key);
      return translated === key ? fallback : translated;
    },
    [t],
  );

  useEffect(() => {
    const handleReset = () => {
      void refreshData();
    };

    window.addEventListener('paperTradingReset', handleReset);
    return () => {
      window.removeEventListener('paperTradingReset', handleReset);
    };
  }, [refreshData]);

  const handleResetModalClose = useCallback(() => {
    setShowResetModal(false);
  }, []);

  const unrealizedPnL = useMemo(() => parseNumeric(rawUnrealizedPnL) ?? 0, [rawUnrealizedPnL]);
  const baseBalance = useMemo(() => parseNumeric(rawBaseBalance) ?? 0, [rawBaseBalance]);
  const equity = useMemo(() => baseBalance + unrealizedPnL, [baseBalance, unrealizedPnL]);
  const winRate = useMemo(() => parseNumeric(rawWinRate) ?? 0, [rawWinRate]);
  const connectionStatus = useMemo(
    () => ({
      color: 'bg-green-500',
      label: mode === 'paper'
        ? translateWithFallback('trading.paperTrading', 'Paper')
        : translateWithFallback('dashboard.connection.live', 'Live'),
    }),
    [mode, translateWithFallback],
  );

  const labelClass = isMobile ? 'text-xs text-muted-foreground' : 'text-sm text-muted-foreground';
  const valueClass = isMobile ? 'text-lg font-semibold' : 'text-2xl font-semibold';
  const iconSize = isMobile ? 'w-4 h-4' : 'w-5 h-5';

  const EquityIcon = equity >= baseBalance ? TrendingUp : TrendingDown;
  const PnlIcon = unrealizedPnL >= 0 ? TrendingUp : TrendingDown;

  const realTimeTitle = translateWithFallback('dashboard.realTimePerformance', 'Real-Time Performance');
  const equityLabel = translateWithFallback('trading.equity', 'Equity');
  const unrealizedLabel = translateWithFallback('trading.unrealizedPnL', 'Unrealized P&L');
  const winRateLabel = translateWithFallback('trading.winRate', 'Win Rate');
  const resetLabel = translateWithFallback('paperTradingDashboard.performance.reset', 'Reset');

  return (
    <Card className="shadow-sm border border-border">
      <CardContent className={`${isMobile ? 'p-3' : 'p-4'} space-y-4`}>
        <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between gap-4'}`}>
          <div className="flex items-center gap-2">
            <Activity className={`${iconSize} text-orange-500`} />
            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-orange-600 dark:text-orange-400`}>
              {realTimeTitle}
            </h3>
          </div>
          <div className={`flex items-center ${isMobile ? 'justify-between gap-2' : 'gap-3'}`}>
            <Badge variant="secondary" className="flex items-center gap-2 px-2 py-1">
              <span className={`h-2 w-2 rounded-full ${connectionStatus.color}`} />
              <span className={isMobile ? 'text-xs font-medium' : 'text-sm font-medium'}>
                {connectionStatus.label}
              </span>
            </Badge>
            {mode === 'paper' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResetModal(true)}
                className={`${isMobile ? 'text-xs px-2 py-1' : ''} text-orange-600 border-orange-300 hover:bg-orange-50`}
              >
                <RotateCcw className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                {!isMobile && <span className="ml-1">{resetLabel}</span>}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3 transition-colors duration-300">
            <div className="space-y-1">
              <p className={labelClass}>{equityLabel}</p>
              <p className={`${valueClass} text-orange-600 dark:text-orange-400`}>{formatCurrency(equity)}</p>
            </div>
            <EquityIcon className={`${iconSize} text-orange-500`} />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3 transition-colors duration-300">
            <div className="space-y-1">
              <p className={labelClass}>{unrealizedLabel}</p>
              <p className={`${valueClass} ${getPerformanceColor(unrealizedPnL)}`}>
                {formatCurrency(unrealizedPnL)}
              </p>
            </div>
            <PnlIcon className={`${iconSize} ${unrealizedPnL >= 0 ? 'text-green-500' : 'text-red-500'}`} />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3 transition-colors duration-300">
            <div className="space-y-1">
              <p className={labelClass}>{winRateLabel}</p>
              <p className={`${valueClass} text-gray-600 dark:text-gray-300`}>{`${winRate.toFixed(1)}%`}</p>
            </div>
          </div>
        </div>
      </CardContent>
      {mode === 'paper' && <PaperTradingReset isOpen={showResetModal} onClose={handleResetModalClose} />}
    </Card>
  );
};

export default RealTimePerformanceMetrics;
